import dayjs from 'dayjs';
import { getCurrentCalendarDate, getYesterdayCalendarDate } from '@features/regional-overview/utils/current-calendar-date.utils';
import type { FlightStagePredictions } from 'querys/regional-overview/regional-overview.query.models';

export interface PredictionColumn {
  gridColumnStart: number;
  gridColumnEnd: number;
  prediction: FlightStagePredictions;
}

export const generateLast7DaysKeys = (): string[] => {
  const today = dayjs();
  const keys = [];
  for (let i = 3; i >= -3; i--) {
    keys.push(today.subtract(i, 'day').format('YYYY-MM-DD'));
  }
  return keys;
};

export const generateNext7DaysKeys = (): string[] => {
  const today = dayjs();
  const keys = [];
  for (let i = 0; i < 7; i++) {
    keys.push(today.add(i, 'day').format('YYYY-MM-DD'));
  }
  return keys;
};

export const generateDateKeysForFilter = (selectedDays: number): string[] => {
  const observedEndDate = getYesterdayCalendarDate();
  const predictionStartDate = getCurrentCalendarDate();
  const keys = [];

  for (let i = selectedDays - 1; i >= 0; i--) {
    keys.push(observedEndDate.subtract(i, 'day').format('YYYY-MM-DD'));
  }

  for (let i = 0; i < 6; i++) {
    keys.push(predictionStartDate.add(i, 'day').format('YYYY-MM-DD'));
  }

  return keys;
};

const calculateStartIndex = (predictionStart: dayjs.Dayjs, rangeStart: dayjs.Dayjs, dateKeys: string[]): number => {
  if (predictionStart.isBefore(rangeStart)) {
    return 0;
  }
  return dateKeys.findIndex(dateKey => dayjs(dateKey).isSame(predictionStart, 'day'));
};

const calculateEndIndex = (predictionEnd: dayjs.Dayjs, rangeEnd: dayjs.Dayjs, dateKeys: string[]): number => {
  if (predictionEnd.isAfter(rangeEnd)) {
    return dateKeys.length - 1;
  }
  return dateKeys.findIndex(dateKey => dayjs(dateKey).isSame(predictionEnd, 'day'));
};

const hasDateOverlap = (
  predictionStart: dayjs.Dayjs,
  predictionEnd: dayjs.Dayjs,
  rangeStart: dayjs.Dayjs,
  rangeEnd: dayjs.Dayjs
): boolean => {
  return predictionStart.isBefore(rangeEnd.add(1, 'day')) && predictionEnd.isAfter(rangeStart.subtract(1, 'day'));
};

export const mapPredictionsToColumns = (
  predictions: FlightStagePredictions[] | null | undefined,
  dateKeys: string[]
): PredictionColumn[] => {
  if (!predictions || predictions.length === 0) {
    return [];
  }

  const columns: PredictionColumn[] = [];
  const rangeStart = dayjs(dateKeys[0]);
  const rangeEnd = dayjs(dateKeys.at(-1));

  for (const prediction of predictions) {
    const predictionStart = dayjs(prediction.start_date);
    const predictionEnd = dayjs(prediction.end_date);

    if (hasDateOverlap(predictionStart, predictionEnd, rangeStart, rangeEnd)) {
      const startIndex = calculateStartIndex(predictionStart, rangeStart, dateKeys);
      const endIndex = calculateEndIndex(predictionEnd, rangeEnd, dateKeys);

      if (startIndex !== -1 && endIndex !== -1 && startIndex <= endIndex) {
        columns.push({
          gridColumnStart: startIndex + 1,
          gridColumnEnd: endIndex + 2,
          prediction
        });
      }
    }
  }

  return columns;
};
